import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getStorageFiles, deleteStorageFile } from "@/api/admin.api";
import type { StorageFile } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { HardDrive, Trash2, Image, Video, File } from "lucide-react";

function formatBytes(bytes: number) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

const FOLDERS = ["", "items", "logos", "covers", "backgrounds", "videos"];
const FOLDER_LABELS: Record<string, string> = {
  "": "Tumu", items: "Urunler", logos: "Logolar", covers: "Kapaklar", backgrounds: "Arkaplanlar", videos: "Videolar",
};

function FileIcon({ file }: { file: StorageFile }) {
  if (file.key.match(/\.(mp4|webm|mov)$/i)) return <Video className="size-4 text-purple-500" />;
  if (file.key.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i)) return <Image className="size-4 text-blue-500" />;
  return <File className="size-4 text-gray-500" />;
}

function isImage(key: string) {
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(key);
}

export function StoragePage() {
  const qc = useQueryClient();
  const [folder, setFolder] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-storage", folder],
    queryFn: () => getStorageFiles(folder || undefined),
  });

  const delMutation = useMutation({
    mutationFn: deleteStorageFile,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-storage"] }),
  });

  const folderStats = data?.files.reduce((acc, f) => {
    acc[f.folder] = (acc[f.folder] || 0) + f.size;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HardDrive className="size-6" />
          Dosya Yonetimi
        </h1>
        {data && (
          <div className="text-right">
            <p className="text-2xl font-bold">{formatBytes(data.totalSize)}</p>
            <p className="text-xs text-muted-foreground">{data.files.length} dosya</p>
          </div>
        )}
      </div>

      {/* Folder stats */}
      {data && !folder && Object.keys(folderStats).length > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Object.entries(folderStats).sort((a, b) => b[1] - a[1]).map(([f, size]) => (
            <Card key={f} className="cursor-pointer hover:border-primary transition-colors" onClick={() => setFolder(f)}>
              <CardContent className="py-3 text-center">
                <p className="text-sm font-semibold">{FOLDER_LABELS[f] || f}</p>
                <p className="text-lg font-bold text-primary">{formatBytes(size)}</p>
                <p className="text-xs text-muted-foreground">{data.files.filter(ff => ff.folder === f).length} dosya</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex gap-2 flex-wrap">
        {FOLDERS.map((f) => (
          <Badge
            key={f}
            variant={folder === f ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => setFolder(f)}
          >
            {FOLDER_LABELS[f] || f}
          </Badge>
        ))}
      </div>

      {/* Files */}
      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : !data?.files.length ? (
        <p className="py-8 text-center text-muted-foreground">Dosya bulunamadi</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data.files.map((file) => (
            <Card key={file.key} className="overflow-hidden">
              {isImage(file.key) && (
                <a href={file.url} target="_blank" rel="noopener noreferrer">
                  <img src={file.url} alt="" className="h-32 w-full object-cover" loading="lazy" />
                </a>
              )}
              <CardContent className="py-3">
                <div className="flex items-start gap-2">
                  <FileIcon file={file} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" title={file.key}>{file.key}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{formatBytes(file.size)}</span>
                      <span>{new Date(file.lastModified).toLocaleDateString("tr-TR")}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{file.folder}</Badge>
                    </div>
                  </div>
                  <ConfirmDialog
                    trigger={<Button variant="ghost" size="icon" className="size-7 shrink-0"><Trash2 className="size-3.5 text-destructive" /></Button>}
                    title="Dosyayi Sil"
                    description={`${file.key} kalici olarak silinecek.`}
                    confirmLabel="Sil"
                    onConfirm={() => delMutation.mutate(file.url)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
